import { forwardRef, useCallback, useEffect, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Button, Dropdown, Space } from 'antd';
import {
	$addUpdateTag,
	$createLineBreakNode,
	$createParagraphNode,
	$createTextNode,
	$getNearestNodeFromDOMNode,
	$getNodeByKey,
	$getRoot,
	$getSelection,
	$hasUpdateTag,
	$isElementNode,
	$isRangeSelection,
	$isTextNode,
	$setSelection,
	BLUR_COMMAND,
	COMMAND_PRIORITY_HIGH,
	createEditor,
	HISTORIC_TAG,
	HISTORY_MERGE_TAG,
	INSERT_LINE_BREAK_COMMAND,
	INSERT_PARAGRAPH_COMMAND,
	KEY_ENTER_COMMAND,
	LineBreakNode,
	PASTE_COMMAND,
	PASTE_TAG,
	SKIP_DOM_SELECTION_TAG,
	TextNode,
} from 'lexical';
import { registerPlainText } from '@lexical/plain-text';
import { createEmptyHistoryState, registerHistory } from '@lexical/history';
import { mergeRegister } from '@lexical/utils';

import FormulaSuggestions, { suggestionStatus } from './FormulaSuggestions';
import useFormulaSuggestions from './useFormulaSuggestions';
import {
	buildTokenMenu,
	flattenTokenNodes,
	formulaLiteralSpans,
	TOKEN_MENU_CLASS,
	TOKEN_RE,
} from './tokenTemplate';
import { choiceLiterals, formulaSegments } from './formulaArguments';
import { parseTemplate } from './tokenTemplate';
import {
	$createFormulaChoiceNode,
	$createFormulaRefNode,
	$isFormulaChoiceNode,
	$isFormulaRefNode,
	FormulaChoiceNode,
	FormulaRefNode,
	editableByEditor,
	labelsByEditor,
	paintPill,
} from './formulaInputNodes';

/**
 * FormulaInput — the formula editor, on Lexical (KPR-539).
 *
 * The owner moved the formula editor off the hand-built contentEditable
 * (TokenInput) onto an editor library (KPE-010 d01) and chose Lexical (d49).
 * TokenInput's caret was the cause of KPR-333: it kept empty text nodes beside
 * every pill so the caret had somewhere to land, and the browser then gave
 * the caret invisible extra stops around each pill. Here the caret is
 * Lexical's: an inline decorator is stepped over in one ArrowLeft/ArrowRight
 * (its plain-text arrow handler), Backspace and Delete beside a pill remove it
 * whole, and Lexical owns the DOM, so there are no stray nodes to stop on.
 *
 * THE STORED FORMULA IS STILL A PLAIN STRING (d03). The editor is a view over
 * it: a value comes in, is parsed into text and reference nodes, and every
 * edit is serialised straight back. A reference is a FormulaRefNode whose
 * text content IS its `{{ref}}` token, so serialising is reading each node's
 * text, and a formula the editor never touched comes back byte for byte.
 *
 * WHY Lexical core and not @lexical/react: the React package pulls yjs,
 * floating-ui and some thirty packages for a composer we need about twenty
 * lines of (create the editor, attach the root, register plain text and
 * history). The pill is drawn by `createDOM`, not a React portal, so it
 * appears in the same commit as the text around it — no frame where a pill is
 * an empty box — and relabelling it is a DOM patch Lexical ignores (it never
 * manages the inside of a decorator).
 *
 * WHY 0.45.0, pinned: every Lexical package from 0.46 on declares an optional
 * `typescript >=5.2` peer, and react-scripts 5 holds TypeScript at 4.9, so
 * `npm ci` refuses the tree (ERESOLVE) unless the repo turns on
 * legacy-peer-deps for every install. A follow-up owns the bump.
 *
 * Same props and ref as TokenInput, so the two formula surfaces swap in place:
 *   value, onChange, tokens, placeholder, ariaLabel, multiline, disabled,
 *   describedBy, invalid; ref → insertText(text, { caretBack }),
 *   insertToken(token), focus().
 * It is always a formula: a `{{…}}` inside a text literal ("…") is text.
 *
 * TYPING SUGGESTS (KPR-332): `suggest` (default true) wires in the suggestion
 * layer — the start of a function name, or `{{`, opens a list under the field
 * (formulaSuggest.js decides what, useFormulaSuggestions.js when and which
 * keys, FormulaSuggestions.js draws it). It is a seam, not part of this file:
 * the next item on this editor (KPR-350) adds a provider and touches none of
 * those. `suggest={false}` turns it off.
 *
 * ARGUMENTS ARE PICKED (KPR-350): inside a call the suggestion list offers
 * what the function's argument takes (formulaArguments.js reads where the
 * caret is; the vendored module's typed params say what goes there): an
 * element, or a unit or date part, which is written as its quoted text and
 * drawn as a VALUE pill (FormulaChoiceNode). A value literal in such an
 * argument becomes its pill on load and when typed by hand, so typing a
 * formula and picking it give the same string and the same pills.
 *
 * A TEMPLATE (KPR-708, `template`): words with pills, not a formula. Card
 * change's composer builds a text from pills and the words typed between them,
 * so a quote is a quote (no value pills, no literals) and the pills are
 * `{{token}}`s the caller words (`tokenLabel(token)`, tried before the
 * vocabulary). With `onPillEdit(token, index, dom)` each pill's words are a
 * button (Tab to it, Enter or Space, or a click) that asks the caller to edit
 * that pill; `ref.replaceToken(index, token)` swaps it back in as one undo
 * step. It is the same editor: undo, removal, paste, the insert menu and the
 * caret rules are not copied. A formula surface passes none of these props.
 *
 * A complete `{{…}}` typed or pasted outside a literal becomes a pill — by its
 * token, or by a label that names exactly one token (`{{Unit price}}`), as
 * TokenInput resolved them. Promotion only ever goes one way: opening a quote
 * in front of a pill does not turn the pill back into text mid-edit (that
 * would make every pill after an unclosed quote flicker into raw braces).
 */

// Tags an update that writes a value given from OUTSIDE (a load, a form
// reset): it emits nothing, runs no typed-reference transform, and is never
// an undo step — undoing past a value the parent replaced would hand it back a
// formula it no longer holds.
const SYNC_TAG = 'keepr-formula-sync';

// The updates that are not the person editing: the suggestions never open for
// them (a value written from outside, an undo or redo, a paste).
const QUIET_TAGS = [SYNC_TAG, HISTORIC_TAG, PASTE_TAG];

// ------------------------------------------------------------ the pill node
// FormulaRefNode and FormulaChoiceNode live in formulaInputNodes.js (re-exported
// here, where every caller finds them).
export { FormulaRefNode, FormulaChoiceNode, $createFormulaRefNode, $isFormulaRefNode, $createFormulaChoiceNode, $isFormulaChoiceNode };

// ------------------------------------------------------- string <-> editor

/**
 * The editor's content as the stored formula string. Blocks are joined by a
 * newline (plain text keeps one paragraph; a second can only come from a
 * stray paste path, and it must still read as a line break, not vanish).
 * Read exactly as held, U+00A0 included: what the person TYPES has its
 * non-breaking spaces made plain by the transform (as TokenInput's read made
 * them), but a stored value comes back byte for byte until it is edited.
 */
export function $readFormula() {
	const out = [];
	$getRoot().getChildren().forEach((block, index) => {
		if (index > 0) out.push('\n');
		if ($isElementNode(block)) block.getChildren().forEach(child => out.push(child.getTextContent()));
		else out.push(block.getTextContent());
	});
	return out.join('');
}

/**
 * Replaces the editor's content with a stored formula string. Returns the
 * keys of the line breaks it wrote: a single-line editor keeps THOSE (a
 * value is drawn as stored) and turns any other into a space.
 */
export function $writeFormula(value, { template = false } = {}) {
	const breaks = [];
	const root = $getRoot();
	root.clear();
	const paragraph = $createParagraphNode();
	// A template is words and pills: a quote is a quote, never a value pill.
	const read = typeof value === 'string' ? value : '';
	(template ? parseTemplate(read) : formulaSegments(read)).forEach(segment => {
		if (segment.type === 'token') {
			paragraph.append($createFormulaRefNode(segment.token));
			return;
		}
		if (segment.type === 'choice') {
			paragraph.append($createFormulaChoiceNode(segment.text, segment.label));
			return;
		}
		// A stored newline is a line break even in a single-line field: a
		// value is drawn as it is, never rewritten on load.
		segment.text.split('\n').forEach((line, index) => {
			if (index > 0) {
				const lineBreak = $createLineBreakNode();
				breaks.push(lineBreak.getKey());
				paragraph.append(lineBreak);
			}
			if (line) paragraph.append($createTextNode(line));
		});
	});
	root.append(paragraph);
	return breaks;
}

/** The whole formula, and where in it `target` starts (-1 when it is not there). */
function $formulaAround(target) {
	let text = '';
	let at = -1;
	$getRoot().getChildren().forEach((block, index) => {
		if (index > 0) text += '\n';
		const children = $isElementNode(block) ? block.getChildren() : [block];
		children.forEach(child => {
			if (child.is(target)) at = text.length;
			text += child.getTextContent();
		});
	});
	return { text, at };
}

// The formula string each committed editor state holds, read once per state.
const committedFormulas = new WeakMap();

/**
 * `node`'s text with a non-breaking space made plain only where this edit put
 * it (KPR-977). The edit's span is what differs between the formula the last
 * committed state held and the one the update holds now, matched from both
 * ends; a U+00A0 outside it is the stored value's own and is kept byte for
 * byte, so editing one place never rewrites another. Located by its place in
 * the whole formula, so a node split or merged by the same update (a typed
 * reference, a deleted pill) cannot lose it. `all` makes the whole node the
 * edit (text an IME composed, which is committed before its transform runs).
 */
function $withoutTypedNonBreakingSpaces(editor, node, all) {
	const text = node.getTextContent();
	const { text: now, at } = $formulaAround(node);
	if (all || at < 0) return text.replace(/\u00a0/g, ' ');
	const state = editor.getEditorState();
	let before = committedFormulas.get(state);
	if (before === undefined) {
		before = state.read($readFormula);
		committedFormulas.set(state, before);
	}
	let start = 0;
	const shortest = Math.min(before.length, now.length);
	while (start < shortest && before[start] === now[start]) start += 1;
	let end = now.length;
	let beforeEnd = before.length;
	while (end > start && beforeEnd > start && now[end - 1] === before[beforeEnd - 1]) {
		end -= 1;
		beforeEnd -= 1;
	}
	return text.replace(/\u00a0/g, (found, offset) => (at + offset >= start && at + offset < end ? ' ' : found));
}

/**
 * A text node holding complete `{{…}}` references outside a formula literal:
 * each becomes a pill, all in one pass. One pass, not one reference per pass:
 * Lexical refuses an update whose transforms keep re-triggering each other
 * (about a hundred rounds), and a pasted formula with two hundred references
 * would trip it. The caret, wherever it was in the text, ends after the pill
 * it was in or beside (`replace` moves a point on the replaced node to just
 * after its successor; `splitText` keeps a point in the part it falls in).
 */
function $promoteTypedReferences(node, vocabulary, { template = false } = {}) {
	if (!node.isSimpleText() || !node.isAttached()) return false;
	const text = node.getTextContent();
	if (!text.includes('{{')) return false;
	const { text: formula, at } = $formulaAround(node);
	if (at < 0) return false;
	const literals = template ? [] : formulaLiteralSpans(formula);
	const inLiteral = offset => literals.some(([start, end]) => at + offset >= start && at + offset < end);
	const found = [];
	const pattern = new RegExp(TOKEN_RE.source, 'g');
	for (let match = pattern.exec(text); match; match = pattern.exec(text)) {
		if (!inLiteral(match.index)) found.push({ start: match.index, end: match.index + match[0].length, whole: match[0] });
	}
	if (!found.length) return false;
	$replaceSpans(node, found, ({ whole }) => {
		const token = vocabulary.known.has(whole) ? whole : (vocabulary.tokenByLabel.get(whole.slice(2, -2).trim()) || whole);
		return $createFormulaRefNode(token);
	});
	return true;
}

/**
 * Splits `node` at every span of `found` ({ start, end, whole }, offsets in
 * the node, in order) and replaces each span's part with `make(span)`, in one
 * pass (see above for why one pass).
 */
function $replaceSpans(node, found, make) {
	const offsets = [];
	found.forEach(({ start, end }) => { offsets.push(start, end); });
	const parts = node.splitText(...offsets);
	// splitText drops a zero offset and a cut at the very end, so the parts are
	// matched back to the spans by where each part starts.
	let cursor = 0;
	const partAt = new Map();
	parts.forEach(part => { partAt.set(cursor, part); cursor += part.getTextContentSize(); });
	found.forEach(span => {
		const part = partAt.get(span.start);
		if (!part || part.getTextContent() !== span.whole) return;
		part.replace(make(span));
	});
}

/**
 * A text node holding a picked value written by hand (KPR-350): a closed
 * literal that is the whole of a unit or date-part argument and spells one of
 * its choices (`convert({{weight}}, "kg")`). Each becomes its value pill, so
 * typing a formula draws it exactly as picking it does. Promote-only, as
 * references are; a literal that is not a choice stays text and the analyser
 * says why.
 *
 * Never a literal the caret is strictly INSIDE: typing `min` between quotes
 * already there passes through `"m"`, which is a choice (Months), and
 * promoting it then would throw the caret out and leave `"m"in`. Such a
 * literal is promoted when it is finished — the caret at or past its closing
 * quote on the next edit — or when the field loses focus (`everywhere`).
 */
function $promoteChoiceLiterals(node, { everywhere = false } = {}) {
	if (!node.isSimpleText() || !node.isAttached()) return;
	const text = node.getTextContent();
	if (!text.includes('"') && !text.includes('\'')) return;
	const { text: formula, at } = $formulaAround(node);
	if (at < 0) return;
	let caret = -1;
	const selection = everywhere ? null : $getSelection();
	if ($isRangeSelection(selection) && selection.isCollapsed() && selection.anchor.type === 'text' && selection.anchor.key === node.getKey()) {
		caret = at + selection.anchor.offset;
	}
	const found = choiceLiterals(formula)
		.filter(choice => choice.start >= at && choice.end <= at + text.length)
		.filter(choice => !(caret > choice.start && caret < choice.end))
		.map(choice => ({ start: choice.start - at, end: choice.end - at, whole: choice.text, label: choice.label }));
	if (!found.length) return;
	$replaceSpans(node, found, ({ whole, label }) => $createFormulaChoiceNode(whole, label));
}

/** The text nodes holding a value literal still drawn as text (see above). */
function $textWithChoices() {
	const out = [];
	$getRoot().getChildren().forEach(block => {
		if (!$isElementNode(block)) return;
		block.getChildren().forEach(child => {
			if (!$isTextNode(child) || !child.isSimpleText()) return;
			const text = child.getTextContent();
			if (!text.includes('"') && !text.includes('\'')) return;
			const { text: formula, at } = $formulaAround(child);
			if (choiceLiterals(formula).some(choice => choice.start >= at && choice.end <= at + text.length)) out.push(child);
		});
	});
	return out;
}

/**
 * Pills holding a LABEL (`{{Unit price}}`) where the vocabulary now has one
 * token for it (`{{price}}`): each with the token it means. A typed label
 * resolves as it is promoted, but a value saved in label form, or one typed
 * before the vocabulary loaded (quick add's `{{me.…}}` arrive late), holds
 * such pills — and TokenInput resolved the WHOLE formula on every edit and on
 * blur, so the stored string would otherwise differ from what it stored.
 */
function $labelPills(vocabulary) {
	const out = [];
	$getRoot().getChildren().forEach(block => {
		if (!$isElementNode(block)) return;
		block.getChildren().forEach(child => {
			if (!$isFormulaRefNode(child)) return;
			const token = child.getToken();
			if (vocabulary.known.has(token)) return;
			const meant = vocabulary.tokenByLabel.get(token.slice(2, -2).trim());
			if (meant && meant !== token) out.push([child, meant]);
		});
	});
	return out;
}

/** A remembered selection is reusable only while every point it names still exists and fits. */
function $selectionStillFits(selection) {
	return [selection.anchor, selection.focus].every(point => {
		const node = $getNodeByKey(point.key);
		if (!node || !node.isAttached()) return false;
		if (point.type === 'text') return $isTextNode(node) && point.offset <= node.getTextContentSize();
		return $isElementNode(node) && point.offset <= node.getChildrenSize();
	});
}

// --------------------------------------------------------------- component

const FormulaInput = forwardRef(function FormulaInput({
	value = '',
	onChange,
	tokens = [],
	placeholder = '',
	ariaLabel = 'Formula',
	multiline = false,
	disabled = false,
	describedBy,
	invalid = false,
	suggest = true,
	template = false,
	tokenLabel = null,
	onPillEdit = null,
}, ref) {
	const text = typeof value === 'string' ? value : '';

	// What the editor last said the formula is. A `value` equal to it is our
	// own echo coming back through the parent and must not repaint (a repaint
	// would throw the caret to the end on every keystroke).
	const lastRef = useRef(text);
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const multilineRef = useRef(multiline);
	multilineRef.current = multiline;
	const templateRef = useRef(template);
	templateRef.current = template;
	const tokenLabelRef = useRef(tokenLabel);
	tokenLabelRef.current = tokenLabel;
	const onPillEditRef = useRef(onPillEdit);
	onPillEditRef.current = onPillEdit;
	const disabledRef = useRef(disabled);
	disabledRef.current = disabled;
	// The last caret the person had: the function picker and the `{{ }}` menu
	// take focus before they insert, and the insert belongs where the caret was.
	const caretRef = useRef(null);
	const historyRef = useRef(null);
	const rootRef = useRef(null);
	// The line breaks a value was written with (see $writeFormula).
	const storedBreaksRef = useRef(new Set());

	// Pill words, and a typed label → its token, from every node of the tree at
	// any depth (a token from the third tier must reopen with the same words).
	// The first definition of a token wins; a label two tokens share resolves
	// to neither — never guessed.
	const vocabulary = useMemo(() => {
		const labelByToken = new Map();
		const tokenByLabel = new Map();
		flattenTokenNodes(tokens).forEach(entry => {
			if (!labelByToken.has(entry.token)) labelByToken.set(entry.token, entry.label || entry.token);
			const label = typeof entry.label === 'string' ? entry.label.trim() : '';
			if (!label) return;
			if (!tokenByLabel.has(label)) tokenByLabel.set(label, entry.token);
			else if (tokenByLabel.get(label) !== entry.token) tokenByLabel.set(label, null);
		});
		return { known: labelByToken, tokenByLabel };
	}, [tokens]);
	const vocabularyRef = useRef(vocabulary);
	vocabularyRef.current = vocabulary;

	// An unknown token still gets a readable pill, its own inner text: hiding it
	// would hide what the formula actually stores.
	const labelFor = useCallback(
		token => (tokenLabelRef.current && tokenLabelRef.current(token))
			|| vocabularyRef.current.known.get(token)
			|| token.slice(2, -2).trim()
			|| token,
		[]
	);

	const [editor] = useState(() => {
		const instance = createEditor({
			namespace: 'keepr-formula',
			nodes: [FormulaRefNode, FormulaChoiceNode],
			theme: { paragraph: 'formula-input__paragraph' },
			// A Lexical invariant failure is a bug in this file: surface it.
			onError: error => { throw error; },
		});
		labelsByEditor.set(instance, labelFor);
		// A template whose pills can be edited (`onPillEdit`) draws them as buttons.
		if (template && onPillEdit) editableByEditor.add(instance);
		return instance;
	});

	const resetHistory = useCallback(() => {
		const history = historyRef.current;
		if (!history) return;
		history.undoStack = [];
		history.redoStack = [];
		// Seeded with what is on screen now, so the very first edit can be undone
		// (Lexical's history only pushes an entry once it holds a current one).
		history.current = { editor, editorState: editor.getEditorState() };
	}, [editor]);

	/** Swaps label pills for their tokens (one history step with the edit). True when there were any. */
	const resolveLabelPills = useCallback(() => {
		const any = editor.getEditorState().read(() => $labelPills(vocabularyRef.current).length > 0);
		if (!any || disabledRef.current) return false;
		editor.update(() => {
			$labelPills(vocabularyRef.current).forEach(([node, token]) => node.replace($createFormulaRefNode(token)));
		}, { tag: HISTORY_MERGE_TAG });
		return true;
	}, [editor]);

	// Behaviour: plain text, history, the typed-reference transform, paste,
	// single-line Enter, and the one place onChange is called.
	useLayoutEffect(() => {
		const history = createEmptyHistoryState();
		historyRef.current = history;
		// Text nodes the transform skipped while an IME was composing: the composed
		// text is committed before the composition ends, so the edit's own span is
		// gone by then, and the whole node counts as the person's input.
		const composedKeys = new Set();
		const onTextNode = node => {
			// Never while an IME is composing: swapping nodes under a
			// composition breaks it. The transform runs again when it ends.
			if ($hasUpdateTag(SYNC_TAG)) return;
			if (editor.isComposing()) {
				composedKeys.add(node.getKey());
				return;
			}
			// A non-breaking space typed or pasted (Option+Space, a copy from a
			// document) is a plain space, as TokenInput stored it. Same length,
			// so the caret does not move. Only what this edit put there: the ones a
			// stored value already held stay byte for byte (KPR-977).
			const composed = composedKeys.delete(node.getKey());
			if (node.isSimpleText() && node.getTextContent().includes('\u00a0')) {
				const text = node.getTextContent();
				const plain = $withoutTypedNonBreakingSpaces(editor, node, composed);
				if (plain !== text) node.setTextContent(plain);
			}
			// References first: the parts they leave are new text nodes, and
			// this transform runs on them again (where a value is promoted).
			if ($promoteTypedReferences(node, vocabularyRef.current, { template: templateRef.current })) return;
			if (!templateRef.current) $promoteChoiceLiterals(node);
		};
		// A single-line formula takes a line break from no path — a drop, an
		// autocorrect, a replacement — not just Enter and paste, which are
		// stopped below; a break the stored value already had stays.
		const onLineBreak = node => {
			if (multilineRef.current || $hasUpdateTag(SYNC_TAG) || storedBreaksRef.current.has(node.getKey())) return;
			node.replace($createTextNode(' '));
		};
		const unregister = mergeRegister(
			registerPlainText(editor),
			registerHistory(editor, history, 300),
			// Leaving the field resolves label pills, as TokenInput's blur did,
			// and draws any value literal still shown as text as its pill (one
			// typed with the caret inside its quotes, or made a value by an edit
			// elsewhere, `({{w}}, "kg")` gaining its `convert`). The string is
			// unchanged, so it emits nothing; it merges into the last undo step.
			editor.registerCommand(BLUR_COMMAND, () => {
				resolveLabelPills();
				if (!disabledRef.current && !templateRef.current && editor.getEditorState().read(() => $textWithChoices().length > 0)) {
					editor.update(() => {
						$addUpdateTag(SYNC_TAG);
						// Focus is leaving: writing the DOM selection back would take
						// it again (the click elsewhere would land in the editor).
						$addUpdateTag(SKIP_DOM_SELECTION_TAG);
						$textWithChoices().forEach(node => $promoteChoiceLiterals(node, { everywhere: true }));
					}, { tag: HISTORY_MERGE_TAG });
				}
				return false;
			}, COMMAND_PRIORITY_HIGH),
			editor.registerCommand(KEY_ENTER_COMMAND, event => {
				if (multilineRef.current) return false;
				if (event) event.preventDefault();
				return true;
			}, COMMAND_PRIORITY_HIGH),
			editor.registerCommand(INSERT_LINE_BREAK_COMMAND, () => !multilineRef.current, COMMAND_PRIORITY_HIGH),
			editor.registerCommand(INSERT_PARAGRAPH_COMMAND, () => !multilineRef.current, COMMAND_PRIORITY_HIGH),
			// Paste is text/plain only — formatting is stripped by construction —
			// and the transform turns every `{{…}}` outside a literal into a pill,
			// so pasting a formula reads exactly like typing it.
			editor.registerCommand(PASTE_COMMAND, event => {
				const transfer = event && (event.clipboardData || event.dataTransfer);
				if (!transfer || typeof transfer.getData !== 'function') return false;
				if (typeof event.preventDefault === 'function') event.preventDefault();
				const pasted = transfer.getData('text/plain');
				if (!pasted || disabledRef.current) return true;
				editor.update(() => {
					const selection = $getSelection();
					if (!$isRangeSelection(selection)) return;
					selection.insertRawText(multilineRef.current ? pasted : pasted.replace(/\r?\n/g, ' '));
				}, { tag: PASTE_TAG });
				return true;
			}, COMMAND_PRIORITY_HIGH),
			editor.registerUpdateListener(({ editorState, tags, dirtyElements, dirtyLeaves }) => {
				const { next, labels } = editorState.read(() => {
					const selection = $getSelection();
					if ($isRangeSelection(selection)) caretRef.current = selection.clone();
					return { next: $readFormula(), labels: $labelPills(vocabularyRef.current).length };
				});
				const root = rootRef.current;
				if (root) root.setAttribute('data-empty', next ? 'false' : 'true');
				if (tags.has(SYNC_TAG)) return;
				// An edit is dirty nodes, or an undo/redo (which replaces the whole
				// state and reports none). A caret move is neither, and must never
				// emit: a click must never hand the parent a rewritten formula.
				const historic = tags.has(HISTORIC_TAG);
				if (!historic && !dirtyElements.size && !dirtyLeaves.size) return;
				// Label pills resolve in the same undo step, then that update emits.
				if (!historic && labels && resolveLabelPills()) return;
				if (next === lastRef.current) return;
				lastRef.current = next;
				if (typeof onChangeRef.current === 'function') onChangeRef.current(next);
			}),
		);
		// Registering a transform runs an update (tagged history-merge) that
		// visits the nodes already there, and Lexical runs it INSIDE the
		// registerNodeTransform call. So the transforms are registered inside
		// this sync-tagged update, which that one joins: opening a formula only
		// draws it. It must not rewrite a non-breaking space, promote, resolve a
		// label or emit (opening a formula never changes it; KPR-977 found the
		// registration outside the tag doing exactly that to the value written
		// at mount). Committing it here also keeps it from still being pending,
		// and folding its tag into, the person's first edit.
		let unregisterTransforms = () => {};
		editor.update(() => {
			$addUpdateTag(SYNC_TAG);
			unregisterTransforms = mergeRegister(
				editor.registerNodeTransform(TextNode, onTextNode),
				editor.registerNodeTransform(LineBreakNode, onLineBreak),
			);
		}, { discrete: true });
		resetHistory();
		return () => {
			unregister();
			unregisterTransforms();
		};
	}, [editor, resetHistory, resolveLabelPills]);

	// The value is written AFTER the root is attached, not before: attaching a
	// root tags the next update `history-merge` and, with nothing pending,
	// that tag lingers onto whatever update comes next — which made the
	// person's first edit impossible to undo. Writing here consumes it, in an
	// update that is not an edit, not an undo step and not echoed to the parent.
	const attachRoot = useCallback(element => {
		rootRef.current = element;
		editor.setRootElement(element);
		if (!element) return;
		element.setAttribute('data-empty', lastRef.current ? 'false' : 'true');
		editor.update(() => {
			$addUpdateTag(SYNC_TAG);
			storedBreaksRef.current = new Set($writeFormula(lastRef.current, { template: templateRef.current }));
			$setSelection(null);
		}, { discrete: true, tag: HISTORIC_TAG });
		caretRef.current = null;
		resetHistory();
	}, [editor, resetHistory]);

	useEffect(() => {
		editor.setEditable(!disabled);
	}, [editor, disabled]);

	// A value replaced from outside repaints; our own echo does not.
	useLayoutEffect(() => {
		if (text === lastRef.current) return;
		const root = rootRef.current;
		const hadFocus = !!root && root.ownerDocument.activeElement === root;
		lastRef.current = text;
		editor.update(() => {
			$addUpdateTag(SYNC_TAG);
			storedBreaksRef.current = new Set($writeFormula(text, { template: templateRef.current }));
			if (hadFocus) $getRoot().selectEnd();
			else $setSelection(null);
		}, { discrete: true, tag: HISTORIC_TAG });
		caretRef.current = null;
		resetHistory();
	}, [editor, text, resetHistory]);

	// Pill words follow the vocabulary (a measure renamed in the chart builder)
	// without touching the value, the caret or the undo stack.
	useLayoutEffect(() => {
		const root = rootRef.current;
		if (!root) return;
		root.querySelectorAll('[data-token]').forEach(pill => paintPill(pill, labelFor(pill.getAttribute('data-token'))));
	}, [vocabulary, labelFor, tokenLabel]);

	// Lexical puts the caret back where its selection says; the DOM element
	// then takes focus if it does not hold it yet (@lexical/react's own
	// autofocus does the same two steps).
	const focusEditor = useCallback(() => {
		editor.focus(() => {
			const root = rootRef.current;
			if (!root) return;
			const active = root.ownerDocument.activeElement;
			if (!active || !root.contains(active)) root.focus({ preventScroll: true });
		});
	}, [editor]);

	/**
	 * Runs `insert` at the caret the person last had — or at the end, when the
	 * editor never had one — then gives the editor focus with the caret after
	 * what was inserted.
	 */
	const insertAtCaret = useCallback(insert => {
		if (disabledRef.current) return;
		editor.update(() => {
			let selection = $getSelection();
			if (!$isRangeSelection(selection)) {
				const remembered = caretRef.current;
				if (remembered && $selectionStillFits(remembered)) $setSelection(remembered.clone());
				else $getRoot().selectEnd();
				selection = $getSelection();
			}
			if ($isRangeSelection(selection)) insert(selection);
		}, { discrete: true });
		focusEditor();
	}, [editor, focusEditor]);

	const insertText = useCallback((inserted, { caretBack = 0 } = {}) => {
		if (typeof inserted !== 'string' || !inserted) return;
		insertAtCaret(selection => {
			selection.insertRawText(multilineRef.current ? inserted : inserted.replace(/\r?\n/g, ' '));
			// A function chip parks the caret between its parens: `round(|)`.
			const after = $getSelection();
			if (caretBack > 0 && $isRangeSelection(after) && after.isCollapsed() && after.anchor.type === 'text') {
				const node = after.anchor.getNode();
				const offset = Math.max(0, after.anchor.offset - caretBack);
				after.setTextNodeRange(node, offset, node, offset);
			}
		});
	}, [insertAtCaret]);

	const insertToken = useCallback(token => {
		insertAtCaret(selection => {
			const pill = $createFormulaRefNode(token);
			selection.insertNodes([pill]);
			// `(0, 0)`: with no offsets Lexical selects the END of a text node that
			// follows, so a pill put in the middle of a formula threw the caret past
			// the rest of it (KPR-975). Offset 0 is just after the pill. When the
			// next sibling is a pill, a line break or nothing, the offsets are
			// ignored and the caret goes where it always did.
			pill.selectNext(0, 0);
		});
	}, [insertAtCaret]);

	// Swaps the nth pill (in reading order) for another, as one undo step: a
	// template's pill edited from outside (KPR-708 sets a transform this way).
	const replaceToken = useCallback((index, token) => {
		if (disabledRef.current || typeof token !== 'string') return;
		editor.update(() => {
			const pills = [];
			$getRoot().getChildren().forEach(block => {
				if ($isElementNode(block)) block.getChildren().forEach(child => { if ($isFormulaRefNode(child)) pills.push(child); });
			});
			if (pills[index]) pills[index].replace($createFormulaRefNode(token));
		}, { discrete: true });
	}, [editor]);

	useImperativeHandle(ref, () => ({
		insertText,
		insertToken,
		replaceToken,
		focus: focusEditor,
		// For tests and for the editors built on this one (KPR-332, KPR-350).
		editor,
	}), [insertText, insertToken, replaceToken, focusEditor, editor]);

	// A template's pill is a button (see editableByEditor): its click, or Enter
	// or Space on it, asks the caller to edit that pill, naming it by its place
	// among the pills (a token may stand twice).
	const editPill = useCallback(trigger => {
		const pillDom = trigger.closest('[data-token]');
		const root = rootRef.current;
		const handler = onPillEditRef.current;
		if (!pillDom || !root || typeof handler !== 'function' || disabledRef.current) return;
		const index = Array.from(root.querySelectorAll('[data-token]')).indexOf(pillDom);
		handler(pillDom.getAttribute('data-token'), index, pillDom);
	}, []);

	// Enter and Space on a pill's button belong to the button. Caught before the
	// editor sees them: its Enter would put a line break at the caret instead.
	const handleKeyDownCapture = useCallback(event => {
		if (event.key !== 'Enter' && event.key !== ' ') return;
		const trigger = event.target && event.target.closest ? event.target.closest('[data-token-edit]') : null;
		if (!trigger) return;
		event.preventDefault();
		event.stopPropagation();
		editPill(trigger);
	}, [editPill]);

	// The × on a pill removes it; the caret stays where the pill was.
	const handleClick = useCallback(event => {
		const edit = event.target && event.target.closest ? event.target.closest('[data-token-edit]') : null;
		if (edit) {
			event.preventDefault();
			editPill(edit);
			return;
		}
		const trigger = event.target && event.target.closest ? event.target.closest('[data-token-remove]') : null;
		if (!trigger) return;
		event.preventDefault();
		if (disabledRef.current) return;
		const pillDom = trigger.closest('[data-token], [data-choice]');
		if (!pillDom) return;
		editor.update(() => {
			const node = $getNearestNodeFromDOMNode(pillDom);
			if (!$isFormulaRefNode(node) && !$isFormulaChoiceNode(node)) return;
			node.selectPrevious();
			node.remove();
		}, { discrete: true });
	}, [editor, editPill]);

	const { items: menuItems, tokenByKey } = useMemo(() => buildTokenMenu(tokens), [tokens]);

	// Typing suggests (KPR-332): the function being typed, or an element after
	// `{{`. The list reacts to the editor's own updates, never to a load, an
	// undo, a paste or a value from outside (the tags below).
	const suggestions = useFormulaSuggestions({
		editor,
		enabled: suggest && !disabled,
		tokens,
		readFormula: $readFormula,
		createPill: $createFormulaRefNode,
		createChoice: $createFormulaChoiceNode,
		quietTags: QUIET_TAGS,
	});

	const handleMenuClick = useCallback(({ key, domEvent }) => {
		// rc-menu fires onClick for leaves, and for a group that is ALSO
		// insertable (a card-lookup), which resolves to its token.
		const token = tokenByKey.get(key);
		if (!token) return;
		// A template is often several lines (a long text): the Enter that picked
		// the item must not also reach the editor it hands focus to, where it
		// would put a line break after the pill.
		if (templateRef.current && domEvent && domEvent.type === 'keydown' && typeof domEvent.preventDefault === 'function') domEvent.preventDefault();
		insertToken(token);
	}, [tokenByKey, insertToken]);

	const className = [
		'token-input__editor',
		'formula-input__editor',
		multiline ? 'token-input__editor--multiline' : '',
		disabled ? 'token-input__editor--disabled' : '',
	].filter(Boolean).join(' ');

	return (
		<Space.Compact
			className={`input-affix-group token-input formula-input${disabled ? ' token-input--disabled' : ''}`}
			style={{ width: '100%' }}
		>
			{/* The frame is the flex item, not the editor: Chrome focuses a
			    contenteditable that is a flex child when the empty space beside
			    it is clicked (Lexical warns about exactly this). React never
			    renders children inside the editor: Lexical owns its DOM. */}
			<FormulaSuggestions suggest={suggestions} ariaLabel={ariaLabel}>
				<div className="formula-input__frame" onKeyDownCapture={handleKeyDownCapture}>
					<div
						ref={attachRoot}
						className={className}
						contentEditable={!disabled}
						suppressContentEditableWarning
						role="textbox"
						aria-label={ariaLabel}
						aria-multiline={multiline ? 'true' : 'false'}
						aria-disabled={disabled ? 'true' : undefined}
						aria-describedby={describedBy || undefined}
						aria-invalid={invalid ? 'true' : undefined}
						aria-autocomplete={suggest && !disabled ? 'list' : undefined}
						aria-haspopup={suggest && !disabled ? 'listbox' : undefined}
						aria-controls={suggestions.open ? suggestions.listId : undefined}
						aria-activedescendant={suggestions.open ? suggestions.activeId : undefined}
						// A contenteditable is focusable already; the attribute is what
						// an element naming `aria-activedescendant` must carry.
						tabIndex={disabled ? -1 : 0}
						data-placeholder={placeholder}
						spellCheck={false}
						onClick={handleClick}
					/>
					{/* Always in the page, so a screen reader hears the count when the list opens. */}
					<span className="formula-suggest__status" role="status" aria-live="polite">{suggestionStatus(suggestions)}</span>
				</div>
			</FormulaSuggestions>
			{/* Affix convention: the action sits rightmost, outside the editable
			    region — a button inside the editor is a caret trap. */}
			<Dropdown
				trigger={['click']}
				rootClassName={TOKEN_MENU_CLASS}
				// The menu takes focus when it opens, so the arrow keys and Enter
				// pick an element: without it focus stays on the button and the
				// menu is mouse-only (TokenInput's menu still is).
				autoFocus
				disabled={disabled || !menuItems.length}
				menu={{
					items: menuItems,
					onClick: handleMenuClick,
					// Submenus open on click: a hover-opened tier is unreachable by
					// keyboard and unusable on touch (TokenInput's ruling).
					triggerSubMenuAction: 'click',
				}}
			>
				<Button
					className="token-input__insert"
					disabled={disabled || !menuItems.length}
					aria-label={`Insert a token into ${ariaLabel}`}
					onMouseDown={event => event.preventDefault()}
				>
					{'{{ }}'}
				</Button>
			</Dropdown>
		</Space.Compact>
	);
});

export default FormulaInput;
